import { singleton } from 'tsyringe';
import { ContentRecommendationUserAction } from '../../shared/constants';
import { DelegateProvider } from '../../shared/interfaces/delegate-provider.interface';
import {
  kNameOfUserActionService,
  IUserActionService,
  IUserActionServiceDelegate,
} from '../../shared/interfaces/user-action-service.interface';
import { PromotionDisplayDetails } from '../../shared/models/promotion-display-details.model';
import { TrackEvent } from '../../shared/models/tracking-event.model';
import { TimerService } from '../../shared/services/timer.service';
import { FunctionPropertyNames } from '../../shared/types';
import { OWExtensionWrapper } from '../ow-odk-wrappers/ow-extension-wrapper';
import { OWUtilsWrapper } from '../ow-odk-wrappers/ow-utils-wrapper';
import { OWInternalSettingsWrapper } from '../ow-odk-wrappers/ow-internal-settings-wrapper';
import { PromotionAnalyticsService } from './promotion-analytics/promotion-analytics.service';
import { PromotionType } from './datastore-service/models/raw-promotion.model';
import { OWCustomURLWrapper } from '../../shared/wrappers/ow-custom-url-wrapper';

// -----------------------------------------------------------------------------
@singleton()
export class UserActionService
  implements IUserActionService, DelegateProvider<IUserActionServiceDelegate>
{
  private readonly listeners: Set<IUserActionServiceDelegate>;

  // ---------------------------------------------------------------------------
  public constructor(
    private readonly owExtensionWrapper: OWExtensionWrapper,
    private readonly owUtilsWrapper: OWUtilsWrapper,
    private readonly owInternalSettingsWrapper: OWInternalSettingsWrapper,
    private readonly owCustomURLWrapper: OWCustomURLWrapper,
    private readonly promotionAnalyticsService: PromotionAnalyticsService,
    private readonly timerService: TimerService,
  ) {
    this.listeners = new Set<IUserActionServiceDelegate>();
    this.owExtensionWrapper.initialize();
  }

  // ---------------------------------------------------------------------------
  // ExposableService
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  get token(): string {
    return kNameOfUserActionService;
  }
  // ---------------------------------------------------------------------------

  private triggerListenerEvent(
    eventName: FunctionPropertyNames<IUserActionServiceDelegate>,
    param: any,
  ) {
    if (!eventName) {
      return;
    }

    for (const listener of this.listeners) {
      if (!listener[eventName]) {
        continue;
      }

      listener[eventName](param);
    }
  }

  // ---------------------------------------------------------------------------
  // DelegateProvider
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  addListener(listener: IUserActionServiceDelegate) {
    this.listeners.add(listener);
  }

  // ---------------------------------------------------------------------------
  removeListener(listener: IUserActionServiceDelegate) {
    this.listeners.delete(listener);
  }

  // ---------------------------------------------------------------------------
  // userActionService
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  sendTrackingEvent(event: TrackEvent) {
    this.promotionAnalyticsService.reportContentViewEvent(event);
  }

  // ---------------------------------------------------------------------------
  sendErrorTrackingEvent(promotionInfo: PromotionDisplayDetails) {
    this.promotionAnalyticsService.reportContentViewError(
      promotionInfo.gameId,
      promotionInfo.promotionId,
      promotionInfo.promotionType,
    );
  }

  // ---------------------------------------------------------------------------
  promotionDisplayed(promotionInfo: PromotionDisplayDetails) {
    this.triggerListenerEvent('onUserActionDisplayed', promotionInfo);
  }

  // ---------------------------------------------------------------------------
  promotionDismissed(promotionInfo: PromotionDisplayDetails) {
    if (promotionInfo.promotionType === PromotionType.Offer) {
      this.reportPromotionOfferDismissed(promotionInfo);
    } else {
      this.reportPromotionDismissed(promotionInfo);
    }
  }

  // ---------------------------------------------------------------------------
  promotionOfferApproved(promotionInfo: PromotionDisplayDetails) {
    this.promotionAnalyticsService.reportOfferAction(
      promotionInfo.gameId,
      promotionInfo.promotionId,
      ContentRecommendationUserAction.OfferApproved,
    );

    this.triggerListenerEvent('onUserActionApproved', promotionInfo);
  }

  // ---------------------------------------------------------------------------
  OpenExternalUrl(url: string, activePromotionInfo: PromotionDisplayDetails) {
    // this.owUtilsWrapper.openUrlInDefaultBrowser(url);
    this.promotionAnalyticsService.reportContentRecommend(
      activePromotionInfo.gameId,
      activePromotionInfo.promotionId,
      activePromotionInfo.promotionType,
      ContentRecommendationUserAction.CTAClicked,
    );

    this.triggerListenerEvent('onUserActionCTAClicked', activePromotionInfo);
  }

  // ---------------------------------------------------------------------------
  openNotificationSettings(activePromotionInfo: PromotionDisplayDetails) {
    this.promotionAnalyticsService.reportContentRecommend(
      activePromotionInfo.gameId,
      activePromotionInfo.promotionId,
      activePromotionInfo.promotionType,
      ContentRecommendationUserAction.OpenedNotificationSettings,
    );

    this.owCustomURLWrapper.openNotificationsSettings();
  }

  // ---------------------------------------------------------------------------
  async turnOffPromotions(activePromotionInfo: PromotionDisplayDetails) {
    this.promotionAnalyticsService.reportContentRecommend(
      activePromotionInfo.gameId,
      activePromotionInfo.promotionId,
      activePromotionInfo.promotionType,
      ContentRecommendationUserAction.TurnedOffPromotions,
    );

    await this.owInternalSettingsWrapper.setDisableInGameRecommendations(true);

    this.promotionAnalyticsService.reportPromotionSettingChanged(false);
  }

  // ---------------------------------------------------------------------------
  private reportPromotionDismissed(promotionInfo: PromotionDisplayDetails) {
    this.promotionAnalyticsService.reportContentRecommend(
      promotionInfo.gameId,
      promotionInfo.promotionId,
      promotionInfo.promotionType,
      ContentRecommendationUserAction.Dismissed,
    );

    this.triggerListenerEvent('onUserActionDismissed', promotionInfo);
  }

  // ---------------------------------------------------------------------------
  private reportPromotionOfferDismissed(
    promotionInfo: PromotionDisplayDetails,
  ) {
    this.promotionAnalyticsService.reportOfferAction(
      promotionInfo.gameId,
      promotionInfo.promotionId,
      ContentRecommendationUserAction.OfferDismissed,
    );

    this.triggerListenerEvent('onUserActionDismissed', promotionInfo);
  }
}
